'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { Clock, PoundSterling, CheckCircle2, FileDown, TrendingDown } from 'lucide-react';

const proofPoints = [
  {
    icon: Clock,
    title: 'Weeks, not months',
    description:
      'Structured workflow designed to compress what typically runs as a multi-month consultancy cycle into a focused 2–4 week assessment.',
    color: 'accent',
  },
  {
    icon: PoundSterling,
    title: 'Fraction of consultancy cost',
    description:
      'External CAF engagements typically cost £15,000–£50,000 each. CAF-Pilot is built to reduce that dependency by putting the assessment process in-house.',
    color: 'success',
  },
  {
    icon: CheckCircle2,
    title: '100% CAF 4.0 coverage',
    description:
      'All 41 Contributing Outcomes and ~300 Indicators of Good Practice are structured and linked in the platform — nothing omitted.',
    color: 'accent',
  },
  {
    icon: FileDown,
    title: 'Reports in seconds',
    description:
      'PDF and Excel exports generated on demand, structured for regulator submission — not assembled manually from a spreadsheet.',
    color: 'success',
  },
];

export function TestimonialsSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-mesh-2" />

      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-4 py-1.5"
          >
            <TrendingDown className="h-4 w-4 text-accent" />
            <span className="font-heading text-sm font-medium text-foreground">
              Designed to Compress the CAF Cycle
            </span>
          </motion.div>

          <h2 className="font-display text-3xl tracking-tight-display text-foreground sm:text-4xl lg:text-5xl">
            Designed to compress{' '}
            <span className="italic text-accent">the CAF cycle</span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            CAF-Pilot is built to take assessments that typically run months of
            consultancy down to a structured 2–4 week workflow — replacing
            subjective narrative with measurable, repeatable scoring your team owns.
          </p>
        </motion.div>

        {/* Proof Points */}
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {proofPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:border-accent/30 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 transition-all duration-300 group-hover:scale-110">
                  <Icon className="h-6 w-6 text-accent" strokeWidth={1.5} />
                </div>

                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground">
                  {point.title}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {point.description}
                </p>

                <div className="absolute -bottom-10 -right-10 h-28 w-28 rounded-full bg-accent/5 transition-all duration-500 group-hover:scale-150" />
              </motion.div>
            );
          })}
        </div>

        {/* Clarification note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-muted-foreground/60">
            The above describes what CAF-Pilot is designed and built to do.
            Individual outcomes depend on your organisation&apos;s starting point and assessment scope.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
